import { useEffect, useContext, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import {
    Table,
    Spin,
    Typography,
    Tag,
    message,
    Button,
    Input,
    Row,
    Col,
    Select,
    DatePicker,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import ShowOneAssessment from "./ShowOneAssessment";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

export default function TeamAssessment() {
    const { authData, backUrl } = useContext(AuthContext);

    // Data & Loading
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Show single assessment modal
    const [showParticular, setShowParticular] = useState(false);
    const [selectedData, setSelectedData] = useState(null);

    // Filters
    const [searchName, setSearchName] = useState("");
    const [searchCode, setSearchCode] = useState("");
    const [dateRange, setDateRange] = useState([]);

    const [organisationList, setOrganisationList] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [designationList, setDesignationList] = useState([]);

    const [selectedOrganisation, setSelectedOrganisation] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState("");
    const [selectedDesignation, setSelectedDesignation] = useState("");

    // Fetch Organisations
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisationList(res.data.results);
        } catch (err) {
            console.log("Organisation fetch failed");
        }
    };

    // Fetch Departments by Org
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartmentList(res.data.results);
        } catch (err) {
            console.log("Department fetch failed");
        }
    };

    // Fetch Designations by Dept
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignationList(res.data.results);
        } catch (err) {
            console.log("Designation fetch failed");
        }
    };

    // Fetch Appraisal Tasks
    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backUrl}/api/appraisal/appraisal-tasks/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setData(res.data?.results || []);
            setFilteredData(res.data?.results || []);
            console.log("Fetched tasks:", res.data?.results);
        } catch (error) {
            console.error("Error fetching appraisal tasks:", error);
            message.error("Failed to fetch team appraisal data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchOrganisations();
    }, []);

    // Handlers for filter changes
    const handleOrgChange = (value) => {
        setSelectedOrganisation(value);
        setSelectedDepartment("");
        setSelectedDesignation("");
        setDepartmentList([]);
        setDesignationList([]);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDepartment(value);
        setSelectedDesignation("");
        setDesignationList([]);
        if (value) fetchDesignations(value);
    };

    // Filter logic
    useEffect(() => {
        let temp = [...data];

        if (searchName.trim()) {
            temp = temp.filter((item) =>
                (item.employee_name || item.appraisal?.employee?.name || "")
                    .toLowerCase()
                    .includes(searchName.toLowerCase())
            );
        }

        if (searchCode.trim()) {
            temp = temp.filter((item) =>
                (item.employee_code || item.appraisal?.employee?.emp_code || "")
                    .toLowerCase()
                    .includes(searchCode.toLowerCase())
            );
        }

        if (dateRange.length === 2) {
            const [start, end] = dateRange;
            temp = temp.filter((item) => {
                const itemDate = item.attendance_date;
                return itemDate >= start && itemDate <= end;
            });
        }

        if (selectedOrganisation) {
            temp = temp.filter(
                (item) =>
                    String(item.appraisal?.employee?.organisation) ===
                    String(selectedOrganisation)
            );
        }

        if (selectedDepartment) {
            temp = temp.filter(
                (item) =>
                    String(item.appraisal?.employee?.department) ===
                    String(selectedDepartment)
            );
        }

        if (selectedDesignation) {
            temp = temp.filter(
                (item) =>
                    String(item.appraisal?.employee?.designation) ===
                    String(selectedDesignation)
            );
        }

        setFilteredData(temp);
    }, [
        searchName,
        searchCode,
        dateRange,
        selectedOrganisation,
        selectedDepartment,
        selectedDesignation,
        data,
    ]);

    // Export placeholder
    const exportToExcel = () => {
        message.info("Export to Excel functionality not implemented yet.");
    };

    // Table columns
    const columns = [
        {
            title: "Employee Name",
            key: "employee_name",
            render: (text, record) =>
                record.appraisal?.employee?.user?.first_name + " " + record.appraisal?.employee?.user?.last_name || record.employee_name || "N/A",
        },
        {
            title: "Employee Code",
            key: "emp_code",
            render: (text, record) =>
                record.appraisal?.employee?.emp_code || record.employee_code || "—",
        },

        {
            title: "Appraisal Cycle",
            key: "cycle",
            render: (text, record) => record.appraisal?.cycle?.name || "—",
        },
        {
            title: "Status",
            key: "status",
            render: (status, record) => (
                <Tag
                    color={
                        record.status === "Pending"
                            ? "orange"
                            : record.status === "Completed"
                                ? "green"
                                : "blue"
                    }
                >
                    {record.status}
                </Tag>
            ),
        },
        {
            title: "Organisation",
            key: "organisation",
            render: (text, record) =>
                record.appraisal?.employee?.organisation_name || "—",
        },
        {
            title: "Department",
            key: "department",
            render: (text, record) => record.appraisal?.employee?.department_name || "—",
        },
        {
            title: "Designation",
            key: "designation",
            render: (text, record) => record.appraisal?.employee?.designation_name || "—",
        },
        {
            title: "View / Edit",
            key: "view_edit",
            render: (record) => (
                <Button
                    onClick={() => {
                        setSelectedData(record);
                        setShowParticular(true);
                    }}
                >
                    View Assessment
                </Button>
            ),
        },
    ];

    return (
        <>
            {showParticular ? (
                <ShowOneAssessment selectedData={selectedData} />
            ) : (
                <div style={{ padding: 20, }}>
                    <Title level={4}>Team Appraisal Overview</Title>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Input
                                placeholder="Search by Employee Name"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                            />
                        </Col>

                        <Col span={8}>
                            <Input
                                placeholder="Search by Employee Code"
                                value={searchCode}
                                onChange={(e) => setSearchCode(e.target.value)}
                            />
                        </Col>

                        <Col span={8}>
                            <RangePicker
                                style={{ width: "100%" }}
                                onChange={(dates, dateStrings) => setDateRange(dateStrings)}
                            />
                        </Col>
                    </Row>

                    <Row gutter={16} style={{ marginTop: 15, marginBottom: 15 }}>
                        <Col span={8}>
                            <Select
                                style={{ width: "100%" }}
                                placeholder="Select Organisation"
                                value={selectedOrganisation || undefined}
                                onChange={handleOrgChange}
                                allowClear
                            >
                                {organisationList.map((org) => (
                                    <Option key={org.id} value={org.id}>
                                        {org.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        <Col span={8}>
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                }
                                style={{ width: "100%" }}
                                placeholder="Select Department"
                                value={selectedDepartment || undefined}
                                onChange={handleDeptChange}
                                disabled={!selectedOrganisation}
                                allowClear
                            >
                                {departmentList.map((dept) => (
                                    <Option key={dept.id} value={dept.id}>
                                        {dept.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        <Col span={8}>
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    option?.children?.toLowerCase().includes(input.toLowerCase())
                                }
                                style={{ width: "100%" }}
                                placeholder="Select Designation"
                                value={selectedDesignation || undefined}
                                onChange={(v) => setSelectedDesignation(v)}
                                disabled={!selectedDepartment}
                                allowClear
                            >
                                {designationList.map((des) => (
                                    <Option key={des.id} value={des.id}>
                                        {des.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>


                    </Row>

                    {loading ? (
                        <div style={{ textAlign: "center", marginTop: 50 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            className="mt-3."
                            columns={columns}
                            dataSource={filteredData}
                            rowKey={(record) => record.id}
                            pagination={{ pageSize: 5 }}
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th
                                            {...props}
                                            style={{
                                                backgroundColor: "#0e497b",
                                                color: "white",
                                                marginTop: "20px",
                                            }}
                                        >
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                            bordered
                        />
                    )}
                </div>
            )}
        </>
    );
}
